import React, { useState } from 'react';
import { AuthAPI } from '../services/api';
import { UserDB } from '../services/offlineDB';
import './AuthForm.css';

const DEV_LOGIN_EMAIL = process.env.REACT_APP_DEV_LOGIN_EMAIL;
const DEV_LOGIN_PASSWORD = process.env.REACT_APP_DEV_LOGIN_PASSWORD;
const DEVELOPER_BUILD = process.env.REACT_APP_DEVELOPER_BUILD === 'true';

const AuthForm = ({ onAuthSuccess, isLogin = true, onToggleMode, onBack }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleToggleMode = () => {
    setError('');
    setFormData({ email: '', password: '', name: '' });
    if (onToggleMode) onToggleMode();
  };

  const handleDemoLogin = async () => {
    setLoading(true);
    try {
      // Demo mode with mock data
      const demoUser = {
        id: 'demo_user_001',
        email: 'demo@righand.ai',
        name: 'Demo Driver',
        truckerLicense: 'DEMO123'
      };

      await UserDB.saveUserSession(demoUser.id, demoUser.email, demoUser);
      localStorage.setItem('authToken', 'demo_token_12345');
      localStorage.setItem('userId', demoUser.id);
      localStorage.removeItem('righandDeveloperMode');

      onAuthSuccess(demoUser);
    } catch (err) {
      setError('Demo mode failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const canUseDeveloperLogin = () => (
    isLogin
    && DEV_LOGIN_EMAIL
    && DEV_LOGIN_PASSWORD
    && formData.email.trim().toLowerCase() === DEV_LOGIN_EMAIL.trim().toLowerCase()
    && formData.password === DEV_LOGIN_PASSWORD
  );

  const handleDeveloperLogin = async () => {
    const developerUser = {
      id: 'developer_user_001',
      email: DEV_LOGIN_EMAIL,
      name: 'RigHand Developer',
      truckerLicense: 'DEV-LOCAL'
    };

    await UserDB.saveUserSession(developerUser.id, developerUser.email, developerUser);
    localStorage.setItem('authToken', `developer_token_${Date.now()}`);
    localStorage.setItem('userId', developerUser.id);
    localStorage.setItem('righandDeveloperMode', 'true');
    onAuthSuccess(developerUser);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const response = await AuthAPI.login(formData.email, formData.password);
        localStorage.setItem('userId', response.user.id);
        await UserDB.saveUserSession(response.user.id, response.user.email, response.user);
        onAuthSuccess(response.user);
      } else {
        const response = await AuthAPI.register({
          email: formData.email,
          password: formData.password,
          name: formData.name
        });
        localStorage.setItem('userId', response.user.id);
        await UserDB.saveUserSession(response.user.id, response.user.email, response.user);
        onAuthSuccess(response.user);
      }
    } catch (err) {
      if (canUseDeveloperLogin()) {
        await handleDeveloperLogin();
        setLoading(false);
        return;
      }
      const msg = err?.error || err?.message || 'Authentication failed. Check your credentials.';
      setError(msg);
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <img
        src={`${process.env.PUBLIC_URL}/truck-console-bg.png`}
        alt=""
        className="auth-truck-bg"
      />
      <div className="auth-card">
        {onBack && (
          <button type="button" className="auth-back-link" onClick={onBack} aria-label="Back to home">
            ← Back to home
          </button>
        )}
        <h1>🚚 RigHand AI</h1>
        <h2>{isLogin ? 'Driver Login' : 'Register'}</h2>
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="driver@example.com"
              required
            />
          </div>

          {!isLogin && (
            <>
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  required
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              required
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          {!isLogin && (
            <p className="auth-privacy-note">
              By registering you agree to how we handle your data, explained in our{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </p>
          )}

          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Processing...' : (isLogin ? 'Login' : 'Register')}
          </button>
        </form>

        {!DEVELOPER_BUILD && (
        <div className="demo-section">
          <p>Want to test the application?</p>
          <button 
            onClick={handleDemoLogin} 
            disabled={loading}
            className="btn-demo"
          >
            🎯 Demo Mode
          </button>
        </div>
        )}

        <p className="auth-footer">
          {isLogin ? (
            <>
              Don't have an account?{' '}
              <button type="button" className="auth-link-btn" onClick={handleToggleMode}>
                Sign up
              </button>
            </>
          ) : (
            <>
              Already registered?{' '}
              <button type="button" className="auth-link-btn" onClick={handleToggleMode}>
                Log in
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default AuthForm;
